































































































import { Component, Prop, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { mixins } from "vue-class-component";

import CopyToClipboardButton from "@/lib/components/CopyToClipboardButton.vue";
import FormErrors from "@/lib/components/FormErrors.vue";
import LoadingSpinner from "@/lib/components/LoadingSpinner.vue";
import SubmitButton from "@/lib/components/SubmitButton.vue";

import GeneralFormMixin from "@/lib/mixins/GeneralForm";
import dataStore from "@/store";

import ExtraChargeOption from "../extra-charge-option.model";
import PlushieStore from "../store";

@Component({
  components: {
    CopyToClipboardButton,
    FormErrors,
    LoadingSpinner,
    SubmitButton,
  },
})
export default class ExtraChargePaymentLinkWidget extends mixins(
  GeneralFormMixin
) {
  @Prop({ required: true })
  public readonly plushieId!: string;

  public selectedCodes: string[] = [];
  public quantities: Record<string, number> = {};

  private fIsOptionsLoading = false;
  private fOptionsLoadError: string[] = [];
  private fPaymentLinkUrl = "";
  private fPlushieDataStore: PlushieStore;

  constructor() {
    super();
    this.fPlushieDataStore = getModule(PlushieStore, dataStore);
  }

  get isOptionsLoading(): boolean {
    return this.fIsOptionsLoading;
  }

  get optionsLoadError(): string[] {
    return this.fOptionsLoadError;
  }

  get options(): ExtraChargeOption[] {
    return this.fPlushieDataStore
      .getExtraChargeOptionsByPlushieId(this.plushieId)
      .filter((option) => option.isSelectable)
      .sort((a, b) => a.upgradeName.localeCompare(b.upgradeName));
  }

  get paymentLinkUrl(): string {
    return this.fPaymentLinkUrl;
  }

  get selectedByUpgradeCode(): Record<string, boolean> {
    const map: Record<string, boolean> = {};

    for (const option of this.options) {
      map[option.upgradeCode] = this.selectedCodes.includes(option.upgradeCode);
    }

    return map;
  }

  getSubtotal(option: ExtraChargeOption): number {
    const qty = this.selectedByUpgradeCode[option.upgradeCode]
      ? this.quantities[option.upgradeCode]
      : 1;

    return option.unitPrice * qty;
  }

  get selectedTotal(): number {
    let total = 0;

    for (const code of this.selectedCodes) {
      const option = this.options.find((o) => o.upgradeCode === code);

      if (option) {
        total += option.unitPrice * this.quantities[code];
      }
    }

    return total;
  }

  protected async performSubmit(): Promise<void> {
    if (!this.selectedCodes.length) {
      throw new Error("Please select at least one extra charge.");
    }

    const hasInvalidQuantity = this.selectedCodes.some((code) => {
      const qty = this.quantities[code];
      return !qty || !Number.isInteger(qty) || qty < 1;
    });

    if (hasInvalidQuantity) {
      throw new Error(
        "Each selected extra charge requires a whole-number quantity of 1 or more."
      );
    }

    this.clearPaymentLink();

    const items = this.selectedCodes.map((code) => ({
      upgradeCode: code,
      quantity: this.quantities[code],
    }));

    const url = await this.fPlushieDataStore.requestExtraChargePaymentLink({
      plushieId: this.plushieId,
      items,
    });

    this.fPaymentLinkUrl = url;
  }

  private setDefaultQuantities(): void {
    this.options.forEach((option) => {
      this.$set(this.quantities, option.upgradeCode, 1);
    });
  }

  private clearPaymentLink(): void {
    this.fPaymentLinkUrl = "";
  }

  @Watch("selectedCodes")
  private _onSelectedCodesChange(): void {
    this.clearPaymentLink();
  }

  @Watch("quantities", { deep: true })
  private _onQuantitiesChange(): void {
    this.clearPaymentLink();
  }

  @Watch("plushieId", { immediate: true })
  private async _onPlushieIdChange(): Promise<void> {
    if (!this.plushieId) {
      return;
    }

    this.fIsOptionsLoading = true;
    this.fOptionsLoadError = [];

    try {
      await this.fPlushieDataStore.loadExtraChargeOptionsByPlushieId({
        plushieId: this.plushieId,
      });
      this.setDefaultQuantities();
    } catch (e) {
      this.fOptionsLoadError = this.fErrorConverterService.describeError(e);
    } finally {
      this.fIsOptionsLoading = false;
    }
  }
}
